import React, { useCallback, useEffect, useMemo, useState } from "react";
import api, { authFetch } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import CampoFirma from "@/components/CampoFirma";
import QRBox from "@/components/QRBox";
import { toast } from "sonner";
import {
  GraduationCap, Plus, Download, Trash2, Users, Clock, CalendarDays, ArrowLeft,
  CheckCircle2, AlertTriangle, Lock, Unlock, Printer, Edit, PenLine, X,
  ListChecks, HelpCircle, RotateCcw, BookOpen, Award, FileSignature,
  BadgeCheck, Upload, ExternalLink, FolderOpen,
} from "lucide-react";
import { fileUrl } from "@/lib/placeholders";

const itDate = (iso) => (iso ? String(iso).substring(0, 10).split("-").reverse().join("/") : "");

const STATO_META = {
  programmata: { label: "Programmata", colore: "bg-slate-100 text-slate-700" },
  in_corso: { label: "In corso", colore: "bg-amber-100 text-amber-800" },
  chiusa: { label: "Chiusa", colore: "bg-emerald-100 text-emerald-700" },
};

// Come sta messa una persona rispetto al corso che si sta per fare.
const STATO_PERSONA = {
  manca: { label: "non l'ha mai fatto", colore: "text-red-600", peso: 0 },
  scaduto: { label: "scaduto", colore: "text-amber-700", peso: 1 },
  valido: { label: "in corso di validità", colore: "text-emerald-700", peso: 2 },
};

/**
 * La sezione del formatore.
 *
 * Riccardo eroga la formazione ai nostri: qui tiene il catalogo di quello che
 * puo' fare, programma le edizioni, sceglie chi ci va e raccoglie le firme di
 * ingresso e di uscita giornata per giornata. Il registro esce di conseguenza,
 * con le ore che ognuno ha fatto davvero.
 */
export default function Formatore() {
  const [apertaId, setApertaId] = useState(null);

  if (apertaId) {
    return <Edizione id={apertaId} onIndietro={() => setApertaId(null)} />;
  }

  return (
    <div className="space-y-6" data-testid="formatore-page">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-orange-600 mb-2">Dipendenti</div>
        <h1 className="font-display text-4xl font-black tracking-tighter">Formatore</h1>
        <p className="text-sm text-slate-500 mt-1">
          La formazione erogata in casa: corsi, edizioni, registro delle presenze e firme in aula
        </p>
      </div>

      <Tabs defaultValue="edizioni">
        <TabsList>
          <TabsTrigger value="edizioni">Edizioni</TabsTrigger>
          <TabsTrigger value="catalogo">Catalogo dei corsi</TabsTrigger>
          <TabsTrigger value="credenziali">Le mie credenziali</TabsTrigger>
        </TabsList>
        <TabsContent value="edizioni"><Edizioni onApri={setApertaId} /></TabsContent>
        <TabsContent value="catalogo"><Catalogo /></TabsContent>
        <TabsContent value="credenziali"><Credenziali /></TabsContent>
      </Tabs>
    </div>
  );
}

/* --------------------------------------------------------------- catalogo */

function Catalogo() {
  const [corsi, setCorsi] = useState([]);
  const [rif, setRif] = useState(null);
  const [modifica, setModifica] = useState(null);
  const [domandeDi, setDomandeDi] = useState(null);
  const [programmaDi, setProgrammaDi] = useState(null);
  const [materialiDi, setMaterialiDi] = useState(null);
  const [carico, setCarico] = useState(true);

  const carica = useCallback(async () => {
    try {
      setCarico(true);
      const [a, b] = await Promise.all([
        api.get("/formazione/corsi?tutti=true"),
        api.get("/formazione/riferimenti"),
      ]);
      setCorsi(a.data); setRif(b.data);
    } catch { toast.error("Errore nel caricamento del catalogo"); }
    finally { setCarico(false); }
  }, []);
  useEffect(() => { carica(); }, [carica]);

  const proposti = async () => {
    try {
      const { data } = await api.post("/formazione/corsi/proposti");
      toast.success(`${data.aggiunti} corsi messi in catalogo`,
        { description: "Controlla ore e validità: si modificano da qui." });
      carica();
    } catch { toast.error("Errore"); }
  };

  const togli = async (c) => {
    if (!window.confirm(`Togliere «${c.titolo}» dal catalogo?`)) return;
    try {
      const { data } = await api.delete(`/formazione/corsi/${c.id}`);
      toast.success(data.disattivato
        ? "Disattivato: le edizioni già fatte restano"
        : "Eliminato");
      carica();
    } catch { toast.error("Errore"); }
  };

  const perTipologia = useMemo(() => {
    const m = new Map();
    for (const c of corsi) m.set(c.tipologia || "Altro", [...(m.get(c.tipologia || "Altro") || []), c]);
    return [...m.entries()];
  }, [corsi]);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <p className="text-xs text-slate-500 flex-1 min-w-[240px]">
          Quello che puoi erogare, con le ore e la validità dell&apos;attestato.
          Le durate cambiano con la normativa: si correggono qui, e le edizioni
          già fatte restano come erano.
        </p>
        <div className="flex gap-2">
          {rif?.catalogo_vuoto && (
            <Button variant="outline" onClick={proposti} data-testid="corsi-proposti">
              Metti i corsi standard
            </Button>
          )}
          <Button className="bg-orange-500 hover:bg-orange-600 text-white"
                  onClick={() => setModifica({})} data-testid="nuovo-corso">
            <Plus className="w-4 h-4 mr-2" /> Nuovo corso
          </Button>
        </div>
      </div>

      {carico && <p className="text-sm text-slate-500 py-6 text-center">Caricamento...</p>}
      {!carico && corsi.length === 0 && (
        <Card className="border-dashed border-2"><CardContent className="p-8 text-center text-slate-500">
          Il catalogo è vuoto. «Metti i corsi standard» lo riempie con la formazione
          generale, la specifica, l&apos;aggiornamento e i preposti: poi correggi
          quello che non torna e aggiungi le attrezzature.
        </CardContent></Card>
      )}

      {perTipologia.map(([tipologia, righe]) => (
        <div key={tipologia}>
          <div className="text-[11px] uppercase tracking-widest text-slate-400 mb-1">{tipologia}</div>
          <div className="space-y-1.5">
            {righe.map((c) => (
              <Card key={c.id} className={`border-slate-200 ${c.attivo === false ? "opacity-60" : ""}`}>
                <CardContent className="px-3 py-2 flex items-center gap-2.5 flex-wrap">
                  <GraduationCap className="w-4 h-4 text-orange-500 flex-shrink-0" />
                  <div className="flex-1 min-w-[200px]">
                    <div className="flex items-center gap-2 flex-wrap leading-tight">
                      {c.codice && <span className="text-[10px] font-mono text-slate-400">{c.codice}</span>}
                      <span className="font-semibold text-sm">{c.titolo}</span>
                      {c.attivo === false && (
                        <Badge className="bg-slate-200 text-slate-600 border-0 text-[10px]">non più in uso</Badge>
                      )}
                    </div>
                    <div className="text-xs text-slate-500">
                      {c.ore} ore · {c.validita_mesi ? `attestato valido ${c.validita_mesi} mesi` : "attestato senza scadenza"}
                      {c.edizioni ? ` · ${c.edizioni} edizion${c.edizioni === 1 ? "e" : "i"}` : ""}
                      {c.norma ? ` · ${c.norma}` : ""}
                    </div>
                    {c.nota && <div className="text-[11px] text-amber-700">{c.nota}</div>}
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    {(c.programma || []).length > 0 && (
                      <Button variant="outline" size="sm" className="h-7 px-2"
                              onClick={() => setProgrammaDi(c)} title="Programma d'aula">
                        <BookOpen className="w-3.5 h-3.5" />
                      </Button>
                    )}
                    <Button variant="outline" size="sm" className="h-7 px-2"
                            onClick={() => setMaterialiDi(c)} title="Materiali del corso"
                            data-testid={`materiali-${c.id}`}>
                      <FolderOpen className="w-3.5 h-3.5" />
                    </Button>
                    <Button variant="outline" size="sm" className="h-7 px-2"
                            onClick={() => setDomandeDi(c)} title="Domande del test"
                            data-testid={`domande-${c.id}`}>
                      <HelpCircle className="w-3.5 h-3.5" />
                    </Button>
                    <Button variant="outline" size="sm" className="h-7 px-2"
                            onClick={() => setModifica(c)} title="Modifica">
                      <Edit className="w-3.5 h-3.5" />
                    </Button>
                    <Button variant="outline" size="sm" className="h-7 px-2 text-red-600"
                            onClick={() => togli(c)} title="Togli dal catalogo">
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      ))}

      {modifica && (
        <CorsoDialog corso={modifica} tipologie={rif?.tipologie || []}
                     onChiudi={() => setModifica(null)}
                     onSalvato={() => { setModifica(null); carica(); }} />
      )}
      {domandeDi && <DomandeDialog corso={domandeDi} onChiudi={() => setDomandeDi(null)} />}
      {materialiDi && (
        <MaterialiDialog corso={materialiDi} onChiudi={() => setMaterialiDi(null)}
                         onCambiato={carica} />
      )}
      {programmaDi && <ProgrammaDialog corso={programmaDi} onChiudi={() => setProgrammaDi(null)} />}
    </div>
  );
}

function CorsoDialog({ corso, tipologie, onChiudi, onSalvato }) {
  const nuovo = !corso.id;
  const [f, setF] = useState({
    codice: corso.codice || "", titolo: corso.titolo || "",
    tipologia: corso.tipologia || "Attrezzature", ore: corso.ore ?? 4,
    validita_mesi: corso.validita_mesi ?? 60, norma: corso.norma || "",
    categoria_attestato: corso.categoria_attestato || "",
    argomenti: (corso.argomenti || []).join("\n"), nota: corso.nota || "",
    attivo: corso.attivo !== false,
  });
  const [busy, setBusy] = useState(false);

  const salva = async () => {
    if (!f.titolo.trim()) { toast.error("Serve il titolo del corso"); return; }
    setBusy(true);
    try {
      const corpo = { ...f, ore: Number(f.ore) || 0, validita_mesi: Number(f.validita_mesi) || 0,
                      argomenti: f.argomenti.split("\n").map((x) => x.trim()).filter(Boolean) };
      if (nuovo) await api.post("/formazione/corsi", corpo);
      else await api.put(`/formazione/corsi/${corso.id}`, corpo);
      toast.success(nuovo ? "Corso in catalogo" : "Corso aggiornato");
      onSalvato();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onChiudi}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{nuovo ? "Nuovo corso" : "Modifica corso"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Titolo *</Label>
            <Input value={f.titolo} onChange={(e) => setF({ ...f, titolo: e.target.value })}
                   placeholder="es. Carrelli elevatori semoventi" data-testid="corso-titolo" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label>Codice</Label>
              <Input value={f.codice} onChange={(e) => setF({ ...f, codice: e.target.value })}
                     placeholder="es. PLE-10" />
            </div>
            <div>
              <Label>Tipologia</Label>
              <select className="w-full h-10 border border-slate-200 rounded px-2 text-sm"
                      value={f.tipologia} onChange={(e) => setF({ ...f, tipologia: e.target.value })}>
                {tipologie.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label>Ore</Label>
              <Input type="number" step="0.5" value={f.ore}
                     onChange={(e) => setF({ ...f, ore: e.target.value })} />
            </div>
            <div>
              <Label>Validità dell&apos;attestato (mesi)</Label>
              <Input type="number" value={f.validita_mesi}
                     onChange={(e) => setF({ ...f, validita_mesi: e.target.value })} />
              <p className="text-[11px] text-slate-500 mt-0.5">0 = non scade</p>
            </div>
          </div>
          <div>
            <Label>Riferimento normativo</Label>
            <Input value={f.norma} onChange={(e) => setF({ ...f, norma: e.target.value })}
                   placeholder="es. Accordo Stato-Regioni 22/02/2012" />
          </div>
          <div>
            <Label>Categoria dell&apos;attestato nel fascicolo</Label>
            <Input value={f.categoria_attestato}
                   onChange={(e) => setF({ ...f, categoria_attestato: e.target.value })}
                   placeholder="es. PLE" />
            <p className="text-[11px] text-slate-500 mt-0.5">
              Serve a riconoscere chi ce l&apos;ha già: si confronta con gli attestati
              nel fascicolo. Lasciandolo vuoto si usa il titolo del corso.
            </p>
          </div>
          <div>
            <Label>Argomenti (uno per riga)</Label>
            <textarea className="w-full border border-slate-200 rounded px-3 py-2 text-sm min-h-[90px]"
                      value={f.argomenti} onChange={(e) => setF({ ...f, argomenti: e.target.value })} />
          </div>
          <div>
            <Label>Nota</Label>
            <Input value={f.nota} onChange={(e) => setF({ ...f, nota: e.target.value })} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onChiudi}>Annulla</Button>
          <Button onClick={salva} disabled={busy}
                  className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="salva-corso">
            {busy ? "Salvo..." : "Salva"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* --------------------------------------------------------------- edizioni */

function Edizioni({ onApri }) {
  const [righe, setRighe] = useState([]);
  const [stato, setStato] = useState("tutte");
  const [nuova, setNuova] = useState(false);
  const [carico, setCarico] = useState(true);

  const carica = useCallback(async () => {
    try {
      setCarico(true);
      const { data } = await api.get(`/formazione/edizioni?stato=${stato}`);
      setRighe(data);
    } catch { toast.error("Errore nel caricamento delle edizioni"); }
    finally { setCarico(false); }
  }, [stato]);
  useEffect(() => { carica(); }, [carica]);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <select className="h-10 border border-slate-200 rounded px-3 text-sm"
                value={stato} onChange={(e) => setStato(e.target.value)}>
          <option value="tutte">Tutte le edizioni</option>
          <option value="programmata">Programmate</option>
          <option value="in_corso">In corso</option>
          <option value="chiusa">Chiuse</option>
        </select>
        <Button className="bg-orange-500 hover:bg-orange-600 text-white"
                onClick={() => setNuova(true)} data-testid="nuova-edizione">
          <Plus className="w-4 h-4 mr-2" /> Nuova edizione
        </Button>
      </div>

      {carico && <p className="text-sm text-slate-500 py-6 text-center">Caricamento...</p>}
      {!carico && righe.length === 0 && (
        <Card className="border-dashed border-2"><CardContent className="p-8 text-center text-slate-500">
          Nessuna edizione. «Nuova edizione» apre un corso: scegli la giornata o le
          giornate, e le persone da formare.
        </CardContent></Card>
      )}

      <div className="space-y-1.5">
        {righe.map((e) => {
          const meta = STATO_META[e.stato] || STATO_META.programmata;
          const prima = (e.sessioni || [])[0] || {};
          return (
            <Card key={e.id} className="border-slate-200 hover:border-orange-300 transition-colors cursor-pointer"
                  onClick={() => onApri(e.id)} data-testid={`edizione-${e.id}`}>
              <CardContent className="px-3 py-2 flex items-center gap-2.5 flex-wrap">
                <CalendarDays className="w-4 h-4 text-orange-500 flex-shrink-0" />
                <div className="flex-1 min-w-[220px]">
                  <div className="flex items-center gap-2 flex-wrap leading-tight">
                    <span className="font-semibold text-sm">{e.corso?.titolo}</span>
                    <Badge className={`${meta.colore} border-0 text-[10px]`}>{meta.label}</Badge>
                  </div>
                  <div className="text-xs text-slate-500">
                    {itDate(prima.data)}{e.giornate > 1 ? ` · ${e.giornate} giornate` : ""}
                    {" · "}{e.ore_previste} ore · {e.quanti} partecipant{e.quanti === 1 ? "e" : "i"}
                    {e.luogo ? ` · ${e.luogo}` : ""}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {nuova && <NuovaEdizione onChiudi={() => setNuova(false)}
                               onFatta={(id) => { setNuova(false); onApri(id); }} />}
    </div>
  );
}

function NuovaEdizione({ onChiudi, onFatta }) {
  const [corsi, setCorsi] = useState([]);
  const [corsoId, setCorsoId] = useState("");
  const [luogo, setLuogo] = useState("Sede di Riozzo");
  const [sessioni, setSessioni] = useState([
    { data: new Date().toISOString().substring(0, 10), dalle: "08:00", alle: "12:00", argomenti: "" },
  ]);
  const [persone, setPersone] = useState([]);
  const [scelti, setScelti] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/formazione/corsi").then(({ data }) => setCorsi(data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!corsoId) { setPersone([]); return; }
    api.get(`/formazione/candidati?corso_id=${corsoId}`)
      .then(({ data }) => {
        const ordinate = [...data.persone].sort((a, b) =>
          (STATO_PERSONA[a.stato]?.peso ?? 9) - (STATO_PERSONA[b.stato]?.peso ?? 9)
          || (a.full_name || "").localeCompare(b.full_name || ""));
        setPersone(ordinate);
      })
      .catch(() => setPersone([]));
  }, [corsoId]);

  const corso = corsi.find((c) => c.id === corsoId);
  const oreMesse = sessioni.reduce((s, x) => {
    const [a1, a2] = (x.dalle || "0:0").split(":").map(Number);
    const [b1, b2] = (x.alle || "0:0").split(":").map(Number);
    const m = (b1 * 60 + b2) - (a1 * 60 + a2);
    return s + (m > 0 ? m : 0);
  }, 0) / 60;

  const cambia = (i, campo, v) =>
    setSessioni((p) => p.map((s, n) => (n === i ? { ...s, [campo]: v } : s)));

  const salva = async () => {
    if (!corsoId) { toast.error("Scegli il corso"); return; }
    if (!sessioni.length) { toast.error("Metti almeno una giornata"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/formazione/edizioni", {
        corso_id: corsoId, luogo, sessioni, iscritti: scelti,
      });
      toast.success("Edizione aperta");
      onFatta(data.id);
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onChiudi}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Nuova edizione</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Corso *</Label>
            <select className="w-full h-10 border border-slate-200 rounded px-2 text-sm"
                    value={corsoId} onChange={(e) => setCorsoId(e.target.value)}
                    data-testid="scegli-corso">
              <option value="">— scegli —</option>
              {corsi.map((c) => (
                <option key={c.id} value={c.id}>{c.titolo} · {c.ore} ore</option>
              ))}
            </select>
          </div>
          <div>
            <Label>Luogo</Label>
            <Input value={luogo} onChange={(e) => setLuogo(e.target.value)} />
          </div>

          <div>
            <div className="flex items-center justify-between">
              <Label>Giornate</Label>
              <span className={`text-xs ${corso && Math.abs(oreMesse - corso.ore) > 0.01
                ? "text-amber-700" : "text-slate-500"}`}>
                {oreMesse} ore messe{corso ? ` su ${corso.ore} previste` : ""}
              </span>
            </div>
            <div className="space-y-2 mt-1">
              {sessioni.map((s, i) => (
                <div key={i} className="border border-slate-200 rounded p-2 space-y-2">
                  <div className="flex gap-2 items-end flex-wrap">
                    <div className="flex-1 min-w-[130px]">
                      <Label className="text-[11px]">Data</Label>
                      <Input type="date" value={s.data} onChange={(e) => cambia(i, "data", e.target.value)} />
                    </div>
                    <div className="w-24">
                      <Label className="text-[11px]">Dalle</Label>
                      <Input type="time" value={s.dalle} onChange={(e) => cambia(i, "dalle", e.target.value)} />
                    </div>
                    <div className="w-24">
                      <Label className="text-[11px]">Alle</Label>
                      <Input type="time" value={s.alle} onChange={(e) => cambia(i, "alle", e.target.value)} />
                    </div>
                    {sessioni.length > 1 && (
                      <Button variant="outline" size="sm" className="h-10 px-2 text-red-600"
                              onClick={() => setSessioni((p) => p.filter((_, n) => n !== i))}>
                        <X className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </div>
                  <Input placeholder="Argomenti svolti in questa giornata"
                         value={s.argomenti || ""} onChange={(e) => cambia(i, "argomenti", e.target.value)} />
                </div>
              ))}
              <Button variant="outline" size="sm" data-testid="aggiungi-giornata"
                      onClick={() => setSessioni((p) => [...p, {
                        data: p[p.length - 1]?.data || "", dalle: "08:00", alle: "12:00", argomenti: "" }])}>
                <Plus className="w-3.5 h-3.5 mr-1" /> Aggiungi una giornata
              </Button>
            </div>
          </div>

          <div>
            <Label>Chi partecipa {scelti.length > 0 && <span className="text-orange-600">({scelti.length})</span>}</Label>
            {!corsoId && <p className="text-sm text-slate-500 mt-1">Scegli prima il corso.</p>}
            {corsoId && (
              <>
                <p className="text-[11px] text-slate-500 mb-1">
                  In cima chi non l&apos;ha mai fatto e chi ce l&apos;ha scaduto.
                </p>
                <div className="max-h-64 overflow-y-auto border border-slate-200 rounded divide-y divide-slate-100">
                  {persone.map((p) => {
                    const meta = STATO_PERSONA[p.stato] || STATO_PERSONA.manca;
                    const dentro = scelti.includes(p.id);
                    return (
                      <label key={p.id} className="flex items-center gap-2 px-2 py-1.5 cursor-pointer hover:bg-slate-50">
                        <input type="checkbox" checked={dentro}
                               data-testid={`iscritto-${p.id}`}
                               onChange={() => setScelti((s) =>
                                 dentro ? s.filter((x) => x !== p.id) : [...s, p.id])} />
                        <span className="text-sm flex-1">{p.full_name}</span>
                        <span className={`text-[11px] ${meta.colore}`}>
                          {meta.label}{p.scadenza ? ` · ${itDate(p.scadenza)}` : ""}
                        </span>
                      </label>
                    );
                  })}
                  {persone.length === 0 && (
                    <p className="text-sm text-slate-400 p-3">Nessun dipendente in elenco.</p>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onChiudi}>Annulla</Button>
          <Button onClick={salva} disabled={busy || !corsoId}
                  className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="apri-edizione">
            {busy ? "Apro..." : "Apri l'edizione"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ------------------------------------------------------- l'edizione aperta */

function Edizione({ id, onIndietro }) {
  const [dati, setDati] = useState(null);
  const [carico, setCarico] = useState(true);
  const [firmaDi, setFirmaDi] = useState(null);   // {sessione, persona, momento}

  const carica = useCallback(async () => {
    try {
      const { data } = await api.get(`/formazione/edizioni/${id}`);
      setDati(data);
    } catch { toast.error("Edizione non trovata"); onIndietro(); }
    finally { setCarico(false); }
  }, [id, onIndietro]);
  useEffect(() => { carica(); }, [carica]);

  const scarica = async (vuoto) => {
    try {
      const r = await authFetch(`/formazione/edizioni/${id}/registro/pdf${vuoto ? "?vuoto=1" : ""}`);
      if (!r.ok) throw new Error();
      const cd = r.headers.get("content-disposition") || "";
      const nome = /filename="?([^";]+)"?/.exec(cd)?.[1] || "registro.pdf";
      const u = window.URL.createObjectURL(await r.blob());
      const a = document.createElement("a");
      a.href = u; a.download = nome; a.click();
      window.URL.revokeObjectURL(u);
    } catch { toast.error("Errore nel download"); }
  };

  const chiudi = async () => {
    if (!window.confirm("Chiudere l'edizione? Dopo, per correggere una firma, va riaperta.")) return;
    try { await api.post(`/formazione/edizioni/${id}/chiudi`); toast.success("Edizione chiusa"); carica(); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const riapri = async () => {
    try { await api.post(`/formazione/edizioni/${id}/riapri`); toast.success("Riaperta"); carica(); }
    catch { toast.error("Errore"); }
  };

  const annulla = async (sessione, persona, momento) => {
    if (!window.confirm("Annullare questa firma?")) return;
    try {
      await api.delete(`/formazione/edizioni/${id}/firma?sessione_id=${sessione}` +
                       `&employee_id=${persona}&momento=${momento}`);
      carica();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  if (carico) return <p className="text-sm text-slate-500 py-10 text-center">Caricamento...</p>;
  if (!dati) return null;

  const e = dati.edizione;
  const meta = STATO_META[e.stato] || STATO_META.programmata;
  const chiusa = e.stato === "chiusa";
  const presenza = (sid, eid) =>
    dati.presenze.find((p) => p.sessione_id === sid && p.employee_id === eid) || {};

  return (
    <div className="space-y-5" data-testid="edizione-aperta">
      <button type="button" onClick={onIndietro}
              className="text-sm text-slate-500 hover:text-slate-800 flex items-center gap-1">
        <ArrowLeft className="w-4 h-4" /> Tutte le edizioni
      </button>

      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="font-display text-3xl font-black tracking-tighter">{e.corso?.titolo}</h1>
            <Badge className={`${meta.colore} border-0`}>{meta.label}</Badge>
          </div>
          <p className="text-sm text-slate-500 mt-1">
            {e.corso?.tipologia} · {dati.ore_previste} ore su {(e.sessioni || []).length} giornat
            {(e.sessioni || []).length === 1 ? "a" : "e"}
            {e.luogo ? ` · ${e.luogo}` : ""} · docente {e.docente}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={() => scarica(false)} data-testid="registro-pdf">
            <Download className="w-4 h-4 mr-2" /> Registro
          </Button>
          <Button variant="outline" onClick={() => scarica(true)} title="Il foglio firme da stampare in bianco">
            <Printer className="w-4 h-4 mr-2" /> In bianco
          </Button>
          {chiusa
            ? <Button variant="outline" onClick={riapri}><Unlock className="w-4 h-4 mr-2" /> Riapri</Button>
            : <Button onClick={chiudi} className="bg-orange-500 hover:bg-orange-600 text-white"
                      data-testid="chiudi-edizione">
                <Lock className="w-4 h-4 mr-2" /> Chiudi l&apos;edizione
              </Button>}
        </div>
      </div>

      {/* Il conto delle ore: è quello che conta quando si fa l'attestato. */}
      <Card className="border-slate-200">
        <CardContent className="p-3">
          <h3 className="font-display font-bold text-sm flex items-center gap-2 mb-2">
            <Clock className="w-4 h-4 text-orange-500" /> Ore fatte
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-1.5">
            {(e.iscritti || []).map((i) => {
              const c = dati.conto[i.employee_id] || { ore: 0, mancano: [] };
              const completo = c.ore >= dati.ore_previste - 0.01 && dati.ore_previste > 0;
              return (
                <div key={i.employee_id}
                     className={`px-2.5 py-1.5 rounded border text-sm flex items-center gap-2
                                 ${completo ? "border-emerald-200 bg-emerald-50" : "border-amber-200 bg-amber-50"}`}>
                  {completo ? <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                            : <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0" />}
                  <span className="flex-1 truncate">{i.nome}</span>
                  <span className="text-xs font-semibold">{c.ore}/{dati.ore_previste} h</span>
                </div>
              );
            })}
            {(e.iscritti || []).length === 0 && (
              <p className="text-sm text-slate-500">Nessun partecipante iscritto.</p>
            )}
          </div>
        </CardContent>
      </Card>

      <MaterialiEdizione edizione={id} />

      <BloccoTest edizione={id} chiusa={chiusa} />

      <BloccoAttestati edizione={id} firmato={!!e.firma_docente} onCambiato={carica} />

      {(e.sessioni || []).map((s, n) => (
        <Card key={s.id} className="border-slate-200">
          <CardContent className="p-3">
            <div className="flex items-baseline gap-2 flex-wrap mb-2">
              <h3 className="font-display font-bold">Giornata {n + 1}</h3>
              <span className="text-sm text-slate-500">
                {itDate(s.data)} · dalle {s.dalle} alle {s.alle}
              </span>
            </div>
            {s.argomenti && <p className="text-xs text-slate-500 mb-2">{s.argomenti}</p>}
            <div className="space-y-1.5">
              {(e.iscritti || []).map((i) => {
                const p = presenza(s.id, i.employee_id);
                return (
                  <div key={i.employee_id}
                       className="flex items-center gap-2 px-2 py-1.5 border border-slate-200 rounded flex-wrap">
                    <Users className="w-4 h-4 text-slate-400 flex-shrink-0" />
                    <span className="text-sm font-semibold flex-1 min-w-[140px]">{i.nome}</span>
                    {["ingresso", "uscita"].map((momento) => {
                      const fatto = momento === "ingresso" ? p.ingresso : p.uscita;
                      const ora = momento === "ingresso" ? p.ora_ingresso : p.ora_uscita;
                      return (
                        <div key={momento} className="flex items-center gap-1">
                          {fatto ? (
                            <>
                              <Badge className="bg-emerald-100 text-emerald-700 border-0 text-[10px]">
                                {momento} {ora}
                              </Badge>
                              {!chiusa && (
                                <button type="button" title="Annulla la firma"
                                        onClick={() => annulla(s.id, i.employee_id, momento)}
                                        className="text-slate-300 hover:text-red-600">
                                  <X className="w-3.5 h-3.5" />
                                </button>
                              )}
                            </>
                          ) : (
                            <Button variant="outline" size="sm" className="h-7 text-xs px-2"
                                    disabled={chiusa || (momento === "uscita" && !p.ingresso)}
                                    data-testid={`firma-${momento}-${i.employee_id}`}
                                    onClick={() => setFirmaDi({ sessione: s.id, persona: i.employee_id,
                                                                nome: i.nome, momento })}>
                              <PenLine className="w-3.5 h-3.5 mr-1" /> {momento}
                            </Button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      ))}

      {firmaDi && (
        <FirmaDialog edizione={id} dato={firmaDi} onChiudi={() => setFirmaDi(null)}
                     onFirmato={() => { setFirmaDi(null); carica(); }} />
      )}
    </div>
  );
}

function FirmaDialog({ edizione, dato, onChiudi, onFirmato }) {
  const [firma, setFirma] = useState(null);
  const [busy, setBusy] = useState(false);

  const salva = async () => {
    if (!firma) { toast.error("Manca la firma"); return; }
    setBusy(true);
    try {
      await api.post(`/formazione/edizioni/${edizione}/firma`, {
        sessione_id: dato.sessione, employee_id: dato.persona,
        momento: dato.momento, immagine: firma,
      });
      toast.success(`${dato.nome}: ${dato.momento} firmato`);
      onFirmato();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onChiudi}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Firma di {dato.momento} · {dato.nome}</DialogTitle>
        </DialogHeader>
        <CampoFirma valore={firma} onChange={setFirma} etichetta="Firma"
                    descrizione="Passa il dispositivo alla persona: firma con il dito." />
        <DialogFooter>
          <Button variant="outline" onClick={onChiudi}>Annulla</Button>
          <Button onClick={salva} disabled={busy || !firma}
                  className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="salva-firma">
            {busy ? "Salvo..." : "Registra la firma"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* --------------------------------------------------- programma e domande */

/**
 * Il programma d'aula: i moduli con i minuti, quelli dell'Accordo
 * Stato-Regioni. Serve in due momenti - preparare la giornata, e riempire gli
 * "argomenti svolti" del registro - quindi si puo' copiare via.
 */
function ProgrammaDialog({ corso, onChiudi }) {
  const moduli = corso.programma || [];
  const totale = moduli.reduce((s, m) => s + (m.minuti || 0), 0);

  const copia = () => {
    const testo = moduli.map((m) =>
      `${m.modulo} (${m.minuti} min)\n` +
      (m.contenuti || []).map((x) => `  - ${x}`).join("\n")).join("\n\n");
    navigator.clipboard?.writeText(testo)
      .then(() => toast.success("Programma copiato"))
      .catch(() => toast.error("Non sono riuscito a copiarlo"));
  };

  return (
    <Dialog open onOpenChange={onChiudi}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Programma d&apos;aula · {corso.titolo}</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-500">
          {moduli.length} moduli per {Math.round((totale / 60) * 10) / 10} ore.
        </p>
        <div className="space-y-3">
          {moduli.map((m, i) => (
            <div key={i} className="border border-slate-200 rounded p-3">
              <div className="flex items-baseline justify-between gap-2 flex-wrap">
                <span className="font-semibold text-sm">{m.modulo}</span>
                <span className="text-xs text-slate-500">{m.minuti} minuti</span>
              </div>
              <ul className="mt-1 space-y-0.5">
                {(m.contenuti || []).map((x, n) => (
                  <li key={n} className="text-xs text-slate-600">· {x}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={copia}>Copia il programma</Button>
          <Button onClick={onChiudi} className="bg-orange-500 hover:bg-orange-600 text-white">
            Chiudi
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Le domande da cui si pesca il test. Per i corsi dell'articolo 37 sono gia'
 * scritte e si caricano con un pulsante; poi si correggono, si tolgono e se
 * ne aggiungono di proprie - per esempio sul nostro cantiere.
 */
function DomandeDialog({ corso, onChiudi }) {
  const [dati, setDati] = useState(null);
  const [nuova, setNuova] = useState(null);
  const [busy, setBusy] = useState(false);

  const carica = useCallback(async () => {
    try {
      const { data } = await api.get(`/formazione/corsi/${corso.id}/domande`);
      setDati(data);
    } catch { toast.error("Errore nel caricamento delle domande"); }
  }, [corso.id]);
  useEffect(() => { carica(); }, [carica]);

  const proposte = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/formazione/corsi/${corso.id}/domande/proposte`);
      toast.success(`${data.aggiunte} domande caricate`,
        { description: data.gia_presenti ? `${data.gia_presenti} c'erano già` : undefined });
      carica();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };

  const togli = async (d) => {
    if (!window.confirm("Togliere questa domanda?")) return;
    try { await api.delete(`/formazione/domande/${d.id}`); carica(); }
    catch { toast.error("Errore"); }
  };

  const salva = async () => {
    const opzioni = (nuova.opzioni || []).map((x) => (x || "").trim()).filter(Boolean);
    if (!nuova.testo?.trim() || opzioni.length < 2) {
      toast.error("Serve la domanda e almeno due risposte"); return;
    }
    setBusy(true);
    try {
      const corpo = { testo: nuova.testo.trim(), opzioni,
                      giusta: Math.min(nuova.giusta || 0, opzioni.length - 1),
                      argomento: nuova.argomento || null };
      if (nuova.id) await api.put(`/formazione/domande/${nuova.id}`, corpo);
      else await api.post(`/formazione/corsi/${corso.id}/domande`, corpo);
      setNuova(null); carica();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };

  const domande = dati?.domande || [];

  return (
    <Dialog open onOpenChange={onChiudi}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Domande del test · {corso.titolo}</DialogTitle>
        </DialogHeader>

        <div className="flex items-center gap-2 flex-wrap">
          <p className="text-xs text-slate-500 flex-1 min-w-[200px]">
            {domande.length} domande in banca. Il test ne pesca
            {" "}{corso.domande_test || 10} a caso, e si supera con il
            {" "}{corso.soglia || 60}%.
          </p>
          {dati?.proposte > 0 && (
            <Button variant="outline" size="sm" onClick={proposte} disabled={busy}
                    data-testid="domande-proposte">
              <ListChecks className="w-3.5 h-3.5 mr-1" /> Carica le {dati.proposte} già pronte
            </Button>
          )}
          <Button size="sm" className="bg-orange-500 hover:bg-orange-600 text-white"
                  onClick={() => setNuova({ testo: "", opzioni: ["", "", ""], giusta: 0 })}>
            <Plus className="w-3.5 h-3.5 mr-1" /> Domanda
          </Button>
        </div>

        {nuova && (
          <div className="border border-orange-200 bg-orange-50/50 rounded p-3 space-y-2">
            <div>
              <Label>Domanda</Label>
              <textarea className="w-full border border-slate-200 rounded px-3 py-2 text-sm min-h-[60px]"
                        value={nuova.testo} data-testid="testo-domanda"
                        onChange={(e) => setNuova({ ...nuova, testo: e.target.value })} />
            </div>
            <div>
              <Label>Risposte — segna quella giusta</Label>
              {(nuova.opzioni || []).map((o, i) => (
                <div key={i} className="flex items-center gap-2 mt-1">
                  <input type="radio" checked={nuova.giusta === i}
                         onChange={() => setNuova({ ...nuova, giusta: i })} />
                  <Input value={o} placeholder={`Risposta ${i + 1}`}
                         onChange={(e) => setNuova({
                           ...nuova,
                           opzioni: nuova.opzioni.map((x, n) => (n === i ? e.target.value : x)),
                         })} />
                </div>
              ))}
              <Button variant="outline" size="sm" className="mt-1 h-7"
                      onClick={() => setNuova({ ...nuova, opzioni: [...nuova.opzioni, ""] })}>
                <Plus className="w-3 h-3 mr-1" /> Un&apos;altra risposta
              </Button>
            </div>
            <div>
              <Label>Argomento</Label>
              <Input value={nuova.argomento || ""}
                     onChange={(e) => setNuova({ ...nuova, argomento: e.target.value })} />
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" size="sm" onClick={() => setNuova(null)}>Annulla</Button>
              <Button size="sm" onClick={salva} disabled={busy}
                      className="bg-orange-500 hover:bg-orange-600 text-white"
                      data-testid="salva-domanda">Salva</Button>
            </div>
          </div>
        )}

        <div className="space-y-1.5">
          {domande.map((d, n) => (
            <div key={d.id} className="border border-slate-200 rounded px-3 py-2">
              <div className="flex items-start gap-2">
                <span className="text-xs text-slate-400 mt-0.5">{n + 1}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold">{d.testo}</p>
                  <ul className="mt-1 space-y-0.5">
                    {(d.opzioni || []).map((o, i) => (
                      <li key={i} className={`text-xs ${i === d.giusta
                        ? "text-emerald-700 font-semibold" : "text-slate-500"}`}>
                        {i === d.giusta ? "✓ " : "· "}{o}
                      </li>
                    ))}
                  </ul>
                  {d.argomento && (
                    <span className="text-[10px] uppercase tracking-widest text-slate-400">
                      {d.argomento}
                    </span>
                  )}
                </div>
                <div className="flex gap-1 flex-shrink-0">
                  <Button variant="outline" size="sm" className="h-7 px-2"
                          onClick={() => setNuova({ ...d })} title="Modifica">
                    <Edit className="w-3.5 h-3.5" />
                  </Button>
                  <Button variant="outline" size="sm" className="h-7 px-2 text-red-600"
                          onClick={() => togli(d)} title="Togli">
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
          {domande.length === 0 && (
            <p className="text-sm text-slate-500 text-center py-6">
              Nessuna domanda. Per i corsi dell&apos;articolo 37 ce ne sono già pronte:
              caricale e poi correggile come vuoi.
            </p>
          )}
        </div>

        <DialogFooter>
          <Button onClick={onChiudi} className="bg-orange-500 hover:bg-orange-600 text-white">
            Chiudi
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* --------------------------------------------------------- il test in aula */

/**
 * Il test dell'edizione: si apre, compare un codice e un QR, e i partecipanti
 * rispondono dal loro telefono. Qui si vede chi ha consegnato e come è andata.
 */
function BloccoTest({ edizione, chiusa }) {
  const [dati, setDati] = useState(null);
  const [quante, setQuante] = useState(10);
  const [soglia, setSoglia] = useState(60);
  const [busy, setBusy] = useState(false);

  const carica = useCallback(async () => {
    try {
      const { data } = await api.get(`/formazione/edizioni/${edizione}/test/esiti`);
      setDati(data);
    } catch { /* il test puo' non essere mai stato aperto */ }
  }, [edizione]);
  useEffect(() => { carica(); }, [carica]);

  // Mentre il test è aperto le consegne arrivano una dopo l'altra: la pagina
  // si aggiorna da sola, altrimenti si sta lì a ricaricare.
  useEffect(() => {
    if (!dati?.test?.aperto) return undefined;
    const t = setInterval(carica, 8000);
    return () => clearInterval(t);
  }, [dati?.test?.aperto, carica]);

  const apri = async () => {
    setBusy(true);
    try {
      await api.post(`/formazione/edizioni/${edizione}/test`,
                     { quante: Number(quante), soglia: Number(soglia) });
      toast.success("Test aperto");
      carica();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };

  const chiudiTest = async () => {
    try { await api.delete(`/formazione/edizioni/${edizione}/test`); carica(); }
    catch { toast.error("Errore"); }
  };

  const azzera = async (eid, nome) => {
    if (!window.confirm(`Far rifare il test a ${nome}?`)) return;
    try { await api.delete(`/formazione/edizioni/${edizione}/test/esiti/${eid}`); carica(); }
    catch { toast.error("Errore"); }
  };

  const test = dati?.test;
  const indirizzo = `${window.location.origin}/test/${test?.codice || ""}`;

  return (
    <Card className="border-slate-200">
      <CardContent className="p-3">
        <h3 className="font-display font-bold text-sm flex items-center gap-2 mb-2">
          <ListChecks className="w-4 h-4 text-orange-500" /> Verifica dell&apos;apprendimento
        </h3>

        {!test?.codice && (
          <div className="flex items-end gap-2 flex-wrap">
            <div className="w-28">
              <Label className="text-[11px]">Domande</Label>
              <Input type="number" value={quante} onChange={(e) => setQuante(e.target.value)} />
            </div>
            <div className="w-28">
              <Label className="text-[11px]">Si supera con</Label>
              <Input type="number" value={soglia} onChange={(e) => setSoglia(e.target.value)} />
            </div>
            <Button onClick={apri} disabled={busy || chiusa}
                    className="bg-orange-500 hover:bg-orange-600 text-white h-10"
                    data-testid="apri-test">
              Apri il test
            </Button>
            <p className="text-[11px] text-slate-500 w-full">
              Le domande si pescano dalla banca del corso: tutti ricevono lo stesso test.
            </p>
          </div>
        )}

        {test?.codice && (
          <div className="space-y-3">
            <div className="flex gap-4 flex-wrap items-start">
              <div>
                <div className="text-[11px] uppercase tracking-widest text-slate-400">Codice</div>
                <div className="font-display text-4xl font-black tracking-[0.2em]">{test.codice}</div>
                <div className="text-xs text-slate-500 mt-1 break-all">{indirizzo}</div>
                <div className="text-xs text-slate-500">
                  {dati.quante} domande · si supera con il {test.soglia}%
                  {" · "}{test.aperto
                    ? <span className="text-emerald-700 font-semibold">aperto</span>
                    : <span className="text-slate-500">chiuso</span>}
                </div>
                {test.aperto && (
                  <Button variant="outline" size="sm" className="mt-2" onClick={chiudiTest}>
                    <Lock className="w-3.5 h-3.5 mr-1" /> Chiudi il test
                  </Button>
                )}
              </div>
              <div className="w-48">
                <QRBox percorso={`/formazione/edizioni/${edizione}/test/qr`}
                       titolo="Test di apprendimento"
                       sottotitolo={`Codice ${test.codice}`}
                       nomeFile={`test_${test.codice}`} />
              </div>
            </div>

            <div className="space-y-1">
              {(dati.esiti || []).map((r) => (
                <div key={r.employee_id}
                     className={`flex items-center gap-2 px-2.5 py-1.5 rounded border text-sm
                       ${!r.consegnato ? "border-slate-200"
                         : r.superato ? "border-emerald-200 bg-emerald-50"
                                      : "border-red-200 bg-red-50"}`}>
                  <span className="flex-1 truncate">{r.nome}</span>
                  {r.consegnato ? (
                    <>
                      <span className="text-xs font-semibold">
                        {r.punteggio}/{r.su} · {r.percentuale}%
                      </span>
                      <Badge className={`border-0 text-[10px] ${r.superato
                        ? "bg-emerald-600 text-white" : "bg-red-600 text-white"}`}>
                        {r.superato ? "superato" : "non superato"}
                      </Badge>
                      <button type="button" title="Fagli rifare il test"
                              onClick={() => azzera(r.employee_id, r.nome)}
                              className="text-slate-400 hover:text-slate-700">
                        <RotateCcw className="w-3.5 h-3.5" />
                      </button>
                    </>
                  ) : (
                    <span className="text-xs text-slate-400">non ha ancora consegnato</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/* ------------------------------------------------------------ attestati */

/**
 * Gli attestati dell'edizione.
 *
 * Non si genera quello che non si puo' generare: chi non ha fatto le ore o
 * non ha superato il test compare con il motivo scritto, e l'attestato non
 * esce. Quelli emessi finiscono nel fascicolo della persona da soli, con la
 * loro scadenza: da li' entrano nello scadenzario e nel semaforo del
 * tesserino senza che nessuno ricarichi niente.
 */
function BloccoAttestati({ edizione, firmato, onCambiato }) {
  const [dati, setDati] = useState(null);
  const [busy, setBusy] = useState(false);
  const [firma, setFirma] = useState(null);
  const [apriFirma, setApriFirma] = useState(false);

  const carica = useCallback(async () => {
    try {
      const { data } = await api.get(`/formazione/edizioni/${edizione}/attestati`);
      setDati(data);
    } catch { /* niente da dire */ }
  }, [edizione]);
  useEffect(() => { carica(); }, [carica]);

  const genera = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/formazione/edizioni/${edizione}/attestati`);
      if (data.emessi) {
        toast.success(`${data.emessi} attestat${data.emessi === 1 ? "o" : "i"} emess${data.emessi === 1 ? "o" : "i"}`,
          { description: "Sono già nel fascicolo di ognuno, con la scadenza." });
      } else {
        toast.message("Nessun attestato da emettere",
          { description: data.gia_emessi ? "Erano già stati generati." : "Controlla ore e test." });
      }
      carica();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };

  const scarica = async (att) => {
    try {
      const r = await authFetch(`/formazione/attestati/${att.emesso.id}/pdf`);
      if (!r.ok) throw new Error();
      const u = window.URL.createObjectURL(await r.blob());
      const a = document.createElement("a");
      a.href = u; a.download = `Attestato ${att.nome}.pdf`; a.click();
      window.URL.revokeObjectURL(u);
    } catch { toast.error("Errore nel download"); }
  };

  const annulla = async (att) => {
    if (!window.confirm(
      `Annullare l'attestato di ${att.nome}? Sparisce anche dal suo fascicolo.`)) return;
    try {
      await api.delete(`/formazione/attestati/${att.emesso.id}`);
      toast.success("Attestato annullato");
      carica();
    } catch { toast.error("Errore"); }
  };

  const salvaFirma = async () => {
    if (!firma) return;
    try {
      await api.post(`/formazione/edizioni/${edizione}/firma-docente`, { immagine: firma });
      toast.success("Firma registrata", { description: "Va sul registro e su tutti gli attestati." });
      setApriFirma(false); setFirma(null); onCambiato?.();
    } catch { toast.error("Errore"); }
  };

  const righe = dati?.righe || [];
  const pronti = righe.filter((r) => r.puo && !r.emesso).length;
  const emessi = righe.filter((r) => r.emesso).length;

  return (
    <Card className="border-slate-200">
      <CardContent className="p-3">
        <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
          <h3 className="font-display font-bold text-sm flex items-center gap-2">
            <Award className="w-4 h-4 text-orange-500" /> Attestati
          </h3>
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" size="sm" onClick={() => setApriFirma(true)}
                    data-testid="firma-docente">
              <FileSignature className="w-3.5 h-3.5 mr-1" />
              {firmato ? "Cambia la tua firma" : "Metti la tua firma"}
            </Button>
            <Button size="sm" onClick={genera} disabled={busy || !pronti}
                    data-testid="genera-attestati"
                    className={pronti ? "bg-orange-500 hover:bg-orange-600 text-white"
                                      : "bg-slate-100 text-slate-400 hover:bg-slate-100"}>
              {busy ? "Genero..." : pronti ? `Genera ${pronti} attestat${pronti === 1 ? "o" : "i"}`
                                           : "Genera gli attestati"}
            </Button>
          </div>
        </div>

        {!firmato && (
          <p className="text-[11px] text-amber-700 mb-2">
            Non hai ancora messo la tua firma: gli attestati uscirebbero con lo
            spazio in bianco da firmare a penna.
          </p>
        )}
        {dati?.scadenza && (
          <p className="text-[11px] text-slate-500 mb-2">
            Gli attestati di questa edizione scadranno il {itDate(dati.scadenza)}.
          </p>
        )}

        <div className="space-y-1">
          {righe.map((r) => (
            <div key={r.employee_id}
                 className={`flex items-center gap-2 px-2.5 py-1.5 rounded border text-sm flex-wrap
                   ${r.emesso ? "border-emerald-200 bg-emerald-50"
                     : r.puo ? "border-slate-200" : "border-amber-200 bg-amber-50"}`}>
              <span className="flex-1 min-w-[140px] truncate">{r.nome}</span>
              {r.emesso ? (
                <>
                  <span className="text-xs text-slate-500">n. {r.emesso.numero}</span>
                  <Badge className="bg-emerald-600 text-white border-0 text-[10px]">nel fascicolo</Badge>
                  <Button variant="outline" size="sm" className="h-7 px-2"
                          onClick={() => scarica(r)} title="Scarica l'attestato">
                    <Download className="w-3.5 h-3.5" />
                  </Button>
                  <Button variant="outline" size="sm" className="h-7 px-2 text-red-600"
                          onClick={() => annulla(r)} title="Annulla l'attestato">
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </>
              ) : r.puo ? (
                <span className="text-xs text-slate-500">
                  {r.ore}/{r.previste} ore{r.punteggio != null ? ` · test ${r.punteggio}%` : ""} · pronto
                </span>
              ) : (
                <span className="text-xs text-amber-800">{r.perche_no.join(" · ")}</span>
              )}
            </div>
          ))}
          {righe.length === 0 && (
            <p className="text-sm text-slate-500">Nessun partecipante iscritto.</p>
          )}
        </div>
        {emessi > 0 && (
          <p className="text-[11px] text-slate-500 mt-2">
            Gli attestati emessi stanno nel fascicolo di ognuno, sezione attestati.
          </p>
        )}
      </CardContent>

      {apriFirma && (
        <Dialog open onOpenChange={() => setApriFirma(false)}>
          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle>La tua firma di docente</DialogTitle></DialogHeader>
            <CampoFirma valore={firma} onChange={setFirma} etichetta="Firma"
                        descrizione="Si mette una volta e vale per il registro e per tutti gli attestati di questa edizione." />
            <DialogFooter>
              <Button variant="outline" onClick={() => setApriFirma(false)}>Annulla</Button>
              <Button onClick={salvaFirma} disabled={!firma}
                      className="bg-orange-500 hover:bg-orange-600 text-white">
                Registra la firma
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </Card>
  );
}

/* ------------------------------------------------------- le credenziali */

/**
 * Con che titolo insegno, e se sono rimasto aggiornato.
 *
 * E' la prima cosa che chiede chi controlla una formazione fatta in casa:
 * non basta che il corso sia stato fatto, bisogna poter dimostrare che chi
 * l'ha erogato poteva farlo. Qui stanno i titoli con i loro allegati e le
 * ore di aggiornamento, con il conto di quelle fatte nel periodo.
 */
function Credenziali() {
  const [dati, setDati] = useState(null);
  const [nuova, setNuova] = useState(null);
  const [imp, setImp] = useState(null);
  const [busy, setBusy] = useState(false);

  const carica = useCallback(async () => {
    try {
      const { data } = await api.get("/formazione/credenziali");
      setDati(data);
      setImp(data.impostazioni);
    } catch { toast.error("Errore nel caricamento"); }
  }, []);
  useEffect(() => { carica(); }, [carica]);

  const salvaImpostazioni = async () => {
    setBusy(true);
    try {
      await api.put("/formazione/credenziali/impostazioni", {
        ore_richieste: Number(imp.ore_richieste) || 24,
        mesi: Number(imp.mesi) || 36,
        criterio: imp.criterio || null,
        dicitura: imp.dicitura || null,
        conta_docenze: imp.conta_docenze !== false,
      });
      toast.success("Salvato");
      carica();
    } catch { toast.error("Errore"); }
    finally { setBusy(false); }
  };

  const salva = async () => {
    if (!nuova.titolo?.trim()) { toast.error("Serve il titolo"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("tipo", nuova.tipo);
      fd.append("titolo", nuova.titolo.trim());
      for (const k of ["ente", "data", "scadenza", "modalita", "note"]) {
        if (nuova[k]) fd.append(k, nuova[k]);
      }
      if (nuova.ore) fd.append("ore", String(nuova.ore));
      if (nuova.file) fd.append("file", nuova.file);
      await api.post("/formazione/credenziali", fd,
                     { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Registrato");
      setNuova(null); carica();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };

  const togli = async (r) => {
    if (!window.confirm(`Togliere «${r.titolo}»?`)) return;
    try { await api.delete(`/formazione/credenziali/${r.id}`); carica(); }
    catch { toast.error("Errore"); }
  };

  if (!dati) return <p className="text-sm text-slate-500 py-6 text-center">Caricamento...</p>;
  const rip = dati.riepilogo;
  const qual = dati.qualifica || { criterio: null, titoli: 0, a_posto: false };

  return (
    <div className="space-y-4">
      {/* Due cose diverse, due riquadri diversi.
          La qualifica e' il titolo con cui insegni, e non scade con il
          calendario dell'aggiornamento: tenerle insieme faceva leggere «0 ore
          su 24» come «non sei idoneo», che non e' quello che dice. */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <Card className={qual.a_posto ? "border-emerald-300 bg-emerald-50/50" : "border-slate-200"}>
          <CardContent className="p-4 flex items-start gap-3">
            <BadgeCheck className={`w-8 h-8 flex-shrink-0 ${qual.a_posto ? "text-emerald-600" : "text-slate-300"}`} />
            <div className="min-w-0">
              <div className="text-[11px] uppercase tracking-widest text-slate-400">Qualifica</div>
              <div className="font-display text-xl font-black tracking-tighter leading-tight">
                {qual.a_posto ? "Formatore qualificato" : "Da completare"}
              </div>
              <p className="text-sm text-slate-600 mt-0.5">
                {qual.criterio
                  ? `Criterio ${qual.criterio.split(" ")[0]} del D.I. 6 marzo 2013`
                  : "Scegli il criterio qui sotto"}
                {" · "}
                {qual.titoli
                  ? `${qual.titoli} titol${qual.titoli === 1 ? "o" : "i"} registrat${qual.titoli === 1 ? "o" : "i"}`
                  : "nessun titolo caricato"}
              </p>
              {rip.prossima_scadenza && (
                <p className="text-xs text-slate-500 mt-0.5">
                  Primo titolo in scadenza: {itDate(rip.prossima_scadenza)}
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className={rip.a_posto ? "border-emerald-300 bg-emerald-50/50" : "border-amber-300 bg-amber-50/50"}>
          <CardContent className="p-4 flex items-start gap-3">
            <Clock className={`w-8 h-8 flex-shrink-0 ${rip.a_posto ? "text-emerald-600" : "text-amber-600"}`} />
            <div className="min-w-0 flex-1">
              <div className="text-[11px] uppercase tracking-widest text-slate-400">
                Aggiornamento del periodo
              </div>
              <div className="font-display text-xl font-black tracking-tighter leading-tight">
                {rip.ore} ore su {rip.richieste}
              </div>
              <p className="text-sm text-slate-600 mt-0.5">
                {rip.a_posto ? "In regola." : `Ne mancano ${rip.mancano}.`}
                {" "}Dal {itDate(rip.dal)} a oggi.
              </p>
              <p className="text-xs text-slate-500 mt-0.5">
                {rip.ore_docenza > 0 && rip.conta_docenze
                  ? `${rip.ore_docenza} ore erogate da te (${rip.corsi_erogati} corsi) + ${rip.ore_corsi} ore di corsi seguiti.`
                  : `${rip.ore_corsi} ore di corsi seguiti.`}
              </p>
              <p className="text-[11px] text-slate-400 mt-0.5">
                Le ore del corso da formatore non stanno qui: quelle sono il titolo.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Con che titolo insegno */}
      <Card className="border-slate-200">
        <CardContent className="p-3 space-y-3">
          <h3 className="font-display font-bold text-sm">Come sono qualificato</h3>
          <div>
            <Label>Criterio del D.I. 6 marzo 2013</Label>
            <select className="w-full h-10 border border-slate-200 rounded px-2 text-sm"
                    value={imp?.criterio || ""} data-testid="criterio"
                    onChange={(e) => setImp({ ...imp, criterio: e.target.value })}>
              <option value="">— scegli —</option>
              {dati.criteri.map((x) => <option key={x} value={x}>{x}</option>)}
            </select>
          </div>
          <div>
            <Label>Riga da stampare sugli attestati</Label>
            <Input value={imp?.dicitura || ""} data-testid="dicitura"
                   placeholder="Docente qualificato ai sensi del D.I. 6 marzo 2013 · criterio 5"
                   onChange={(e) => setImp({ ...imp, dicitura: e.target.value })} />
            <p className="text-[11px] text-slate-500 mt-0.5">
              Compare in fondo a ogni attestato, sotto la firma. Lasciandola vuota non si stampa.
            </p>
          </div>
          <div className="flex gap-2 items-end flex-wrap">
            <div className="w-32">
              <Label className="text-[11px]">Ore di aggiornamento</Label>
              <Input type="number" value={imp?.ore_richieste ?? 24}
                     onChange={(e) => setImp({ ...imp, ore_richieste: e.target.value })} />
            </div>
            <div className="w-32">
              <Label className="text-[11px]">ogni (mesi)</Label>
              <Input type="number" value={imp?.mesi ?? 36}
                     onChange={(e) => setImp({ ...imp, mesi: e.target.value })} />
            </div>
            <Button onClick={salvaImpostazioni} disabled={busy}
                    className="bg-orange-500 hover:bg-orange-600 text-white h-10"
                    data-testid="salva-credenziali">Salva</Button>
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={imp?.conta_docenze !== false}
                   data-testid="conta-docenze"
                   onChange={(e) => setImp({ ...imp, conta_docenze: e.target.checked })} />
            Conta come aggiornamento anche le ore che erogo io in aula
          </label>
          <p className="text-[11px] text-slate-500">
            Ventiquattro ore ogni tre anni è il conto che si fa di solito:
            verificalo sulla norma in vigore, qui si cambia.
          </p>
        </CardContent>
      </Card>

      {[["qualifica", "I miei titoli", dati.qualifiche],
        ["aggiornamento", "Aggiornamento delle competenze", dati.aggiornamenti]].map(
        ([tipo, titolo, righe]) => (
        <Card key={tipo} className="border-slate-200">
          <CardContent className="p-3">
            <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
              <h3 className="font-display font-bold text-sm">{titolo}</h3>
              <Button variant="outline" size="sm"
                      data-testid={`aggiungi-${tipo}`}
                      onClick={() => setNuova({ tipo, titolo: "", ore: tipo === "aggiornamento" ? 4 : "" })}>
                <Plus className="w-3.5 h-3.5 mr-1" /> Aggiungi
              </Button>
            </div>
            <div className="space-y-1.5">
              {righe.map((r) => (
                <div key={r.id} className="flex items-center gap-2 px-2.5 py-1.5 border border-slate-200 rounded flex-wrap">
                  <div className="flex-1 min-w-[180px]">
                    <div className="text-sm font-semibold">{r.titolo}</div>
                    <div className="text-xs text-slate-500">
                      {[r.ente, r.data ? itDate(r.data) : null, r.modalita,
                        r.ore ? `${r.ore} ore` : null,
                        r.scadenza ? `scade il ${itDate(r.scadenza)}` : null]
                        .filter(Boolean).join(" · ")}
                    </div>
                    {r.note && <div className="text-[11px] text-slate-400">{r.note}</div>}
                  </div>
                  {r.file_url && (
                    <a href={fileUrl(r.file_url)} target="_blank" rel="noopener noreferrer" title="Apri">
                      <Button variant="outline" size="sm" className="h-7 px-2">
                        <ExternalLink className="w-3.5 h-3.5" />
                      </Button>
                    </a>
                  )}
                  <Button variant="outline" size="sm" className="h-7 px-2 text-red-600"
                          onClick={() => togli(r)} title="Togli">
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              ))}
              {righe.length === 0 && (
                <p className="text-sm text-slate-500">
                  {tipo === "qualifica"
                    ? "Niente ancora: metti qui il corso da formatore, la laurea, l'esperienza — con l'allegato."
                    : "Niente ancora: ogni corso, convegno o docenza fatta va qui con le sue ore."}
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      ))}

      <RegistroDocenze />

      {nuova && (
        <Dialog open onOpenChange={() => setNuova(null)}>
          <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {nuova.tipo === "aggiornamento" ? "Aggiornamento" : "Titolo"}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Titolo *</Label>
                <Input value={nuova.titolo} data-testid="titolo-credenziale"
                       onChange={(e) => setNuova({ ...nuova, titolo: e.target.value })}
                       placeholder={nuova.tipo === "aggiornamento"
                         ? "es. Convegno sicurezza in cantiere"
                         : "es. Corso formatori 24 ore"} />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label>Data</Label>
                  <Input type="date" value={nuova.data || ""}
                         onChange={(e) => setNuova({ ...nuova, data: e.target.value })} />
                </div>
                {nuova.tipo === "aggiornamento" ? (
                  <div>
                    <Label>Ore</Label>
                    <Input type="number" step="0.5" value={nuova.ore || ""}
                           onChange={(e) => setNuova({ ...nuova, ore: e.target.value })} />
                  </div>
                ) : (
                  <div>
                    <Label>Scadenza</Label>
                    <Input type="date" value={nuova.scadenza || ""}
                           onChange={(e) => setNuova({ ...nuova, scadenza: e.target.value })} />
                  </div>
                )}
              </div>
              <div>
                <Label>Ente</Label>
                <Input value={nuova.ente || ""}
                       onChange={(e) => setNuova({ ...nuova, ente: e.target.value })} />
              </div>
              {nuova.tipo === "aggiornamento" && (
                <div>
                  <Label>Come</Label>
                  <select className="w-full h-10 border border-slate-200 rounded px-2 text-sm"
                          value={nuova.modalita || ""}
                          onChange={(e) => setNuova({ ...nuova, modalita: e.target.value })}>
                    <option value="">—</option>
                    {["Corso", "Convegno", "Seminario", "Docenza", "Altro"].map((x) =>
                      <option key={x} value={x}>{x}</option>)}
                  </select>
                </div>
              )}
              <div>
                <Label>Note</Label>
                <Input value={nuova.note || ""}
                       onChange={(e) => setNuova({ ...nuova, note: e.target.value })} />
              </div>
              <div>
                <Label>Allegato</Label>
                <input type="file" className="text-sm w-full"
                       onChange={(e) => setNuova({ ...nuova, file: e.target.files?.[0] })} />
                <p className="text-[11px] text-slate-500 mt-0.5">
                  L&apos;attestato, il certificato, la locandina del convegno. Anche una foto.
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setNuova(null)}>Annulla</Button>
              <Button onClick={salva} disabled={busy}
                      className="bg-orange-500 hover:bg-orange-600 text-white"
                      data-testid="salva-credenziale">
                <Upload className="w-4 h-4 mr-2" />{busy ? "Salvo..." : "Registra"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

/**
 * Il registro delle docenze: tutti i corsi erogati, con tipologia, ore e
 * partecipanti. Si costruisce da solo dalle edizioni - non c'e' niente da
 * ricopiare - e serve due volte: per il curriculum da formatore e per
 * rispondere a chi chiede quanta formazione e' stata fatta in azienda.
 */
function RegistroDocenze() {
  const [dati, setDati] = useState(null);

  useEffect(() => {
    api.get("/formazione/docenze").then(({ data }) => setDati(data)).catch(() => {});
  }, []);

  const scarica = async () => {
    try {
      const r = await authFetch("/formazione/docenze/pdf");
      if (!r.ok) throw new Error();
      const u = window.URL.createObjectURL(await r.blob());
      const a = document.createElement("a");
      a.href = u; a.download = "Registro delle docenze.pdf"; a.click();
      window.URL.revokeObjectURL(u);
    } catch { toast.error("Errore nel download"); }
  };

  if (!dati) return null;
  const tot = dati.totali;

  return (
    <Card className="border-slate-200">
      <CardContent className="p-3">
        <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
          <h3 className="font-display font-bold text-sm">Registro delle mie docenze</h3>
          <Button variant="outline" size="sm" onClick={scarica} data-testid="registro-docenze-pdf">
            <Download className="w-3.5 h-3.5 mr-1" /> Registro (PDF)
          </Button>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
          {[["Corsi erogati", tot.corsi], ["Ore in aula", tot.ore],
            ["Partecipanti", tot.partecipanti], ["Ore/persona", tot.ore_persona]].map(
            ([etichetta, valore]) => (
            <div key={etichetta} className="border border-slate-200 rounded px-2.5 py-1.5">
              <div className="text-[10px] uppercase tracking-widest text-slate-400">{etichetta}</div>
              <div className="font-display text-xl font-black tracking-tighter">{valore}</div>
            </div>
          ))}
        </div>

        {dati.righe.length === 0 ? (
          <p className="text-sm text-slate-500">
            Nessun corso erogato finora. Le edizioni compaiono qui da sole, appena
            la prima giornata è passata.
          </p>
        ) : (
          <div className="space-y-1">
            {dati.righe.map((r) => (
              <div key={r.edizione_id}
                   className="flex items-center gap-2 px-2.5 py-1.5 border border-slate-200 rounded text-sm flex-wrap">
                <span className="text-xs text-slate-500 w-20 flex-shrink-0">{itDate(r.data)}</span>
                <span className="flex-1 min-w-[160px] truncate">{r.corso}</span>
                <Badge variant="outline" className="text-[10px]">{r.tipologia}</Badge>
                <span className="text-xs text-slate-500">{r.ore} h</span>
                <span className="text-xs text-slate-500">
                  {r.partecipanti} partecipant{r.partecipanti === 1 ? "e" : "i"}
                </span>
              </div>
            ))}
          </div>
        )}
        <p className="text-[11px] text-slate-500 mt-2">
          «Ore/persona» sono le ore d&apos;aula moltiplicate per i partecipanti:
          è il numero che dice quanta formazione è stata davvero erogata.
        </p>
      </CardContent>
    </Card>
  );
}

/* -------------------------------------------------------- i materiali */

// Come si chiama, in italiano, ogni genere di materiale.
const GENERE_MATERIALE = {
  programma: "Programma del corso",
  guida: "Guida del docente",
  dispensa: "Dispensa",
  slide: "Slide",
  modulo: "Modulo da compilare",
  altro: "Materiale",
};

/**
 * I materiali del corso dentro l'edizione: è quello che serve in aula, e in
 * aula non si ha voglia di cercare. Stanno in cima, si aprono in una scheda
 * nuova e si stampano da lì.
 */
function MaterialiEdizione({ edizione }) {
  const [righe, setRighe] = useState(null);

  useEffect(() => {
    api.get(`/formazione/edizioni/${edizione}/materiali`)
      .then(({ data }) => setRighe(data.materiali || []))
      .catch(() => setRighe([]));
  }, [edizione]);

  if (!righe || righe.length === 0) return null;

  return (
    <Card className="border-slate-200">
      <CardContent className="p-3">
        <h3 className="font-display font-bold text-sm flex items-center gap-2 mb-2">
          <FolderOpen className="w-4 h-4 text-orange-500" /> Materiali per l&apos;aula
        </h3>
        <div className="flex gap-2 flex-wrap">
          {righe.map((m) => (
            <a key={m.id} href={fileUrl(m.url)} target="_blank" rel="noopener noreferrer"
               data-testid={`materiale-${m.id}`}>
              <Button variant="outline" size="sm" className="h-9">
                {m.tipo === "guida" ? <BookOpen className="w-3.5 h-3.5 mr-1.5" />
                                    : <FileSignature className="w-3.5 h-3.5 mr-1.5" />}
                {m.titolo}
              </Button>
            </a>
          ))}
        </div>
        <p className="text-[11px] text-slate-500 mt-2">
          La guida del docente è per te: messaggi chiave, riferimenti di legge e
          attività per ogni punto. Il programma si stampa e si dà ai partecipanti.
        </p>
      </CardContent>
    </Card>
  );
}

/**
 * I materiali visti dal catalogo: quelli in dotazione (che arrivano con
 * l'app e non si cancellano) e quelli che carichi tu - slide, dispense,
 * moduli da compilare.
 */
function MaterialiDialog({ corso, onChiudi, onCambiato }) {
  const [righe, setRighe] = useState([]);
  const [nuovo, setNuovo] = useState(null);
  const [busy, setBusy] = useState(false);

  const carica = useCallback(async () => {
    try {
      const { data } = await api.get(`/formazione/corsi/${corso.id}/materiali`);
      setRighe(data.materiali || []);
    } catch { toast.error("Errore nel caricamento dei materiali"); }
  }, [corso.id]);
  useEffect(() => { carica(); }, [carica]);

  const salva = async () => {
    if (!nuovo.titolo?.trim() || !nuovo.file) {
      toast.error("Servono il titolo e il file"); return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("titolo", nuovo.titolo.trim());
      fd.append("tipo", nuovo.tipo || "dispensa");
      if (nuovo.descrizione) fd.append("descrizione", nuovo.descrizione);
      fd.append("file", nuovo.file);
      await api.post(`/formazione/corsi/${corso.id}/materiali`, fd,
                     { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Materiale caricato");
      setNuovo(null); carica();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };

  const togli = async (m) => {
    if (!window.confirm(`Togliere «${m.titolo}»?`)) return;
    try { await api.delete(`/formazione/materiali/${m.id}`); carica(); }
    catch { toast.error("Errore"); }
  };

  const riprendiProgramma = async () => {
    try {
      const { data } = await api.post(`/formazione/corsi/${corso.id}/programma/modello`);
      toast.success(`Programma aggiornato: ${data.moduli} moduli, ${data.ore} ore`);
      onCambiato?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  return (
    <Dialog open onOpenChange={onChiudi}>
      <DialogContent className="max-w-xl max-h-[92vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Materiali · {corso.titolo}</DialogTitle></DialogHeader>

        <div className="space-y-1.5">
          {righe.map((m) => (
            <div key={m.id} className="flex items-start gap-2 px-2.5 py-2 border border-slate-200 rounded">
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold">{m.titolo}</div>
                <div className="text-[11px] text-slate-500">
                  {GENERE_MATERIALE[m.tipo] || m.tipo}
                  {m.dotazione ? " · in dotazione" : m.filename ? ` · ${m.filename}` : ""}
                </div>
                {m.descrizione && (
                  <div className="text-xs text-slate-500 mt-0.5">{m.descrizione}</div>
                )}
              </div>
              <a href={fileUrl(m.url)} target="_blank" rel="noopener noreferrer" title="Apri">
                <Button variant="outline" size="sm" className="h-7 px-2">
                  <ExternalLink className="w-3.5 h-3.5" />
                </Button>
              </a>
              {!m.dotazione && (
                <Button variant="outline" size="sm" className="h-7 px-2 text-red-600"
                        onClick={() => togli(m)} title="Togli">
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              )}
            </div>
          ))}
          {righe.length === 0 && (
            <p className="text-sm text-slate-500 py-3">
              Per questo corso non ci sono ancora materiali. Caricali qui: slide,
              dispense, moduli da compilare.
            </p>
          )}
        </div>

        {nuovo ? (
          <div className="border border-orange-200 bg-orange-50/50 rounded p-3 space-y-2">
            <div>
              <Label>Titolo</Label>
              <Input value={nuovo.titolo} data-testid="titolo-materiale"
                     onChange={(e) => setNuovo({ ...nuovo, titolo: e.target.value })}
                     placeholder="es. Slide incontro 1" />
            </div>
            <div>
              <Label>Genere</Label>
              <select className="w-full h-10 border border-slate-200 rounded px-2 text-sm"
                      value={nuovo.tipo} onChange={(e) => setNuovo({ ...nuovo, tipo: e.target.value })}>
                {Object.entries(GENERE_MATERIALE).map(([k, v]) =>
                  <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            <div>
              <Label>File</Label>
              <input type="file" className="text-sm w-full"
                     onChange={(e) => setNuovo({ ...nuovo, file: e.target.files?.[0] })} />
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" size="sm" onClick={() => setNuovo(null)}>Annulla</Button>
              <Button size="sm" onClick={salva} disabled={busy}
                      className="bg-orange-500 hover:bg-orange-600 text-white"
                      data-testid="salva-materiale">
                <Upload className="w-3.5 h-3.5 mr-1" />{busy ? "Carico..." : "Carica"}
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" size="sm"
                    onClick={() => setNuovo({ titolo: "", tipo: "dispensa" })}
                    data-testid="aggiungi-materiale">
              <Plus className="w-3.5 h-3.5 mr-1" /> Aggiungi un materiale
            </Button>
            <Button variant="outline" size="sm" onClick={riprendiProgramma}
                    title="Rimette nel corso il programma d'aula dei nostri documenti">
              <RotateCcw className="w-3.5 h-3.5 mr-1" /> Riprendi il programma dal modello
            </Button>
          </div>
        )}

        <DialogFooter>
          <Button onClick={onChiudi} className="bg-orange-500 hover:bg-orange-600 text-white">
            Chiudi
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
